import type { SessionBeat } from "./turo-world-session";
import { L3_LESSONS } from "./turo-world-l3-lessons";

/**
 * Level 3's four sessions — plan §4.3.
 *
 * 🔴 L3 IS GRADED, AND THAT IS THE OPPOSITE OF L2. §4.1: *"L2's no-fail rule was
 * the exception, not the new normal. L3 is ordinary learning: graded beats,
 * normal support ladder, normal spaced repetition."* Per §9 decision 1 it runs
 * on `curriculum/adaptive.ts` + `mastery.ts` — the engine Letter Lagoon and Sky
 * Quest already use — while `engine/warmup` stays the pure no-fail ritual.
 *
 * ⚠️ NO CONSENT BEAT, AND THE ASYMMETRY IS DELIBERATE. L2 opens every session
 * with the camera question because it asks a child to move their body in a
 * lesson ABOUT their body. L3 teaches words. It is also **not adult-gated**
 * (§9 decision 8), so a school can run courtesy without the safety level —
 * §12.4: gating courtesy behind body-safety content is how L2 gets skipped.
 *
 * 🔴 NAMASTE IS FIRST-CLASS, NOT A CULTURAL FOOTNOTE — §4.3, and worth saying to
 * the child directly: it works for hello AND goodbye, morning AND night, with
 * anyone. It is the one greeting that is never wrong.
 */

const talk = (...lines: string[]): SessionBeat => ({
  kind: "talk",
  level: 3,
  lines,
});



/* ─────────────────────────── one continuous flow ── */

/**
 * 🔴 LEVEL 3 IS ONE SITTING, NOT THREE SESSIONS.
 *
 * §4.3 split it into "hello and goodbye", "the magic words" and "all day long",
 * which is how the CONTENT divides — but a child does not experience three
 * greetings, three goodbyes and three "see you next time"s as one lesson. Split
 * that way, every four minutes Turo said hello to a child he was already talking
 * to, and the level felt like being restarted rather than taught.
 *
 * ⚠️ THE ORDER IS STILL THE TEACHING, AND IT IS DELIBERATE:
 *   1. goodbye first — it pairs with the hello a child already knows from L2,
 *      and §4.3's surprise ("the same hands, different words") lands early and
 *      reframes the rest: the hands are easy, the words are the work.
 *   2. the courtesy words, thank you through no thank you.
 *
 * 🔴 SIX PER ROUND, LIKE EVERY GAME, SO THE FOUR TIMES OF DAY ARE HELD BACK —
 * as a unit, because §4.4 says children conflate them and they only teach as a
 * ladder. When they return they go LAST and ADJACENT, ending on night.
 */
const lesson = (l: (typeof L3_LESSONS)[number]): SessionBeat => ({
  kind: "choose",
  level: 3,
  scenarioId: l.id,
  lines: [...l.open, l.teach],
});

const ORDER = [
  "l3-goodbye",
  "l3-thankyou",
  "l3-sorry",
  "l3-excuseme",
  "l3-please",
  "l3-nothankyou",
] as const;

export const L3_FLOW: readonly SessionBeat[] = [
  talk("core.greet", "greet.s1.open"),
  ...ORDER.map((id) => {
    const l = L3_LESSONS.find((x) => x.id === id);
    if (!l) throw new Error(`L3 flow names unknown lesson: ${id}`);
    return lesson(l);
  }),
  talk("greet.s3.close", "core.bye"),
];

/**
 * ⚠️ ONE ENTRY, SO NOTHING ADVANCES A SESSION. `TwRound` rolls to the next
 * session when a beat list ends; with a single list there is nowhere to roll and
 * the level completes — which is what one continuous flow means.
 */
export const L3_SESSIONS: readonly (readonly SessionBeat[])[] = [L3_FLOW];
